import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { InputComponent } from '@frontendlib/components/controls/input/input.component';
import { StitchTimestampsComponent } from '@modules/common/components/stitch-timestamps/stitch-timestamps.component';
import { type Board } from '../../../../types/board';
import { BoardTitleEditor } from '../../../shared/board-title-editor';
import { BoardsEditorStore } from '../../../../services/boards-editor.store';
import { BoardActionsComponent } from '../../../shared/board-actions/board-actions.component';
import { BoardsSaveStatusComponent } from '../../../editor/boards-save-status.component';
import { BOARDS_TITLE_MAX_LENGTH } from '../../../creation/boards-quick-form/boards-quick-form.component';

/**
 * The 3rd-panel record's heading, laid out as the Notes detail header (owner, 2026-11-03): a 40px Board tile beside the inline
 * title and its dates line; Flag, Archive, Trash, Download and share, More, pin badge, Open full board editor and Close at the end
 * of the title's row, 20px icons 8px apart; then Created/Updated (native `app-stitch-timestamps`, as Notes) and the save state at
 * the right (`app-boards-save-status [boardId] [dirty]`). Writes are the existing store paths; nothing here saves on its own.
 */
@Component({
  selector: 'app-board-record-heading',
  templateUrl: './board-record-heading.component.html',
  styleUrl: './board-record-heading.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [BoardActionsComponent, BoardsSaveStatusComponent, IconComponent, InputComponent, MatTooltipModule, StitchTimestampsComponent],
})
export class BoardRecordHeadingComponent {
  public readonly board = input.required<Board>();
  private readonly editor = inject(BoardsEditorStore);
  public readonly openEditor = output<void>();
  /** The guarded open of this board is still loading: the expand button is busy until the editor shows (no second click). */
  protected readonly opening = computed(() => this.editor.opening() === this.board().id);
  public readonly closed = output<void>();

  protected readonly titleMaxLength = BOARDS_TITLE_MAX_LENGTH;
  /** The one Boards title-edit behavior (shared with the editor header): draft, follow-the-library, pending rename, blank put back. */
  private readonly title = new BoardTitleEditor(() => {
    const board = this.board();
    return { id: board.id, title: board.title, readOnly: board.deleted };
  });
  protected readonly titleControl = this.title.control;
  protected readonly titleDirty = this.title.dirty;

  protected saveTitle(): void {
    this.title.save();
  }
}
